import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Popup } from '../components/Popup';
import type { TreasureHunt } from '../types/api';

export function PrizeBadge(): ReactElement {
  const { t } = useTranslation();

  return (
    <span className="inline-block rounded-full bg-prize px-7 py-1.5 text-lg text-ink-950">
      {t('treasureHunt.badge')}
    </span>
  );
}

/** What the giveaway is: the icons to look for, the prizes and the rules. */
export function TreasureHuntIntro({
  hunt,
  open,
  onClose,
}: {
  hunt: TreasureHunt;
  open: boolean;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <Popup open={open} onClose={onClose} label={t('treasureHunt.title')}>
      <PrizeBadge />
      <h2 className="mt-5 text-4xl font-extrabold text-fog-100 uppercase">
        {t('treasureHunt.title')}
      </h2>
      <p className="mt-3 leading-snug text-fog-500">{t('treasureHunt.intro')}</p>
      <p className="mt-2 leading-snug text-fog-500">{t('treasureHunt.howTo')}</p>

      {/* Narrow enough that five icons wrap as three over two, as designed. */}
      <ul className="mx-auto mt-8 flex max-w-[17rem] flex-wrap justify-center gap-6">
        {hunt.icons.map((icon) => (
          <li key={icon.id}>
            <img src={icon.imageUrl} alt={icon.name} className="size-16" />
          </li>
        ))}
      </ul>

      {hunt.prizes.length > 0 && (
        <div className="mt-8 rounded-3xl bg-ink-850 px-5 py-6">
          <h3 className="text-lg text-fog-500">{t('treasureHunt.prizes')}</h3>
          <ul className="mt-4">
            {hunt.prizes.map((prize) => (
              // A short rule centred between prizes, as designed.
              <li
                key={prize}
                className="relative text-lg font-bold text-fog-100 not-first:mt-4 not-first:pt-4 not-first:before:absolute not-first:before:top-0 not-first:before:left-1/2 not-first:before:h-px not-first:before:w-12 not-first:before:-translate-x-1/2 not-first:before:bg-ink-600"
              >
                {prize}
              </li>
            ))}
          </ul>
        </div>
      )}

      {hunt.rulesUrl !== null && (
        <a
          href={hunt.rulesUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="mt-8 inline-block text-fog-500 underline underline-offset-2 hover:text-fog-300"
        >
          {t('treasureHunt.rules')}
        </a>
      )}
    </Popup>
  );
}
